const normalizeOptional = value => {
  const normalized = String(value || '').trim();
  return normalized ? normalized : null;
};

const parseCategoryLine = line => {
  const raw = String(line || '').trim();
  if (!raw) return null;

  try {
    if (raw.startsWith('{') && raw.endsWith('}')) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const name = String(parsed.name || '').trim();
        if (!name) return null;
        const slug = String(parsed.slug || '').trim();
        const description = String(parsed.description || '').trim();
        return {
          name,
          ...(slug ? { slug } : {}),
          ...(description ? { description } : {}),
        };
      }
    }
  } catch {
    // fall through to pipe/comma format
  }

  const parts = raw.split('|').map(item => item.trim());
  const name = String(parts[0] || '').trim();
  if (!name) return null;
  const slug = String(parts[1] || '').trim();
  const description = parts.length > 2 ? parts.slice(2).join('|').trim() : '';
  return {
    name,
    ...(slug ? { slug } : {}),
    ...(description ? { description } : {}),
  };
};

const pushCategory = (collection, seen, category) => {
  if (!category || typeof category !== 'object') return;
  const name = String(category.name || '').trim();
  if (!name) return;
  const slug = String(category.slug || '').trim();
  const description = String(category.description || '').trim();
  const dedupeKey = (slug || name).toLowerCase();
  if (!dedupeKey || seen.has(dedupeKey)) return;
  seen.add(dedupeKey);
  collection.push({
    name,
    ...(slug ? { slug } : {}),
    ...(description ? { description } : {}),
  });
};

const parseCategories = value => {
  const result = [];
  const seen = new Set();

  if (Array.isArray(value)) {
    value.forEach(item => {
      if (item && typeof item === 'object') {
        pushCategory(result, seen, item);
        return;
      }
      pushCategory(result, seen, parseCategoryLine(item));
    });
    return result;
  }

  const raw = String(value || '').trim();
  if (!raw) return result;
  const rows = raw.includes('\n')
    ? raw.split(/\r?\n/g)
    : raw.split(',');
  rows.forEach(row => {
    pushCategory(result, seen, parseCategoryLine(row));
  });
  return result;
};

export function buildCreateDeploymentFormData(values) {
  const categories = parseCategories(values.categories);
  const useTurnstile = Boolean(values.useTurnstile);
  const turnstileSiteKey = String(values.turnstileSiteKey || '').trim();
  const turnstileSecretKey = String(values.turnstileSecretKey || '').trim();
  const sshPrivateKeyPassphrase = String(values.sshPrivateKeyPassphrase || '').trim();
  const payload = {
    domain: String(values.domain || '').trim(),
    sshHost: String(values.sshHost || '').trim(),
    sshPort: Number(values.sshPort || 0),
    sshUsername: String(values.sshUsername || '').trim(),
    sshPassword: String(values.sshPassword || '').trim(),
    sshPrivateKey: String(values.sshPrivateKey || '').trim(),
    ...(sshPrivateKeyPassphrase ? { sshPrivateKeyPassphrase } : {}),
    sitePath: String(values.sitePath || '').trim(),
    siteTitle: String(values.siteTitle || '').trim(),
    blogDescription: String(values.blogDescription || '').trim(),
    siteDescription: String(values.siteDescription || '').trim(),
    adminUser: String(values.adminUser || '').trim(),
    adminEmail: String(values.adminEmail || '').trim(),
    adminPassword: String(values.adminPassword || '').trim(),
    dbHost: String(values.dbHost || '').trim(),
    dbName: String(values.dbName || '').trim(),
    dbUser: String(values.dbUser || '').trim(),
    dbPassword: String(values.dbPassword || '').trim(),
    ...(useTurnstile ? { turnstileSiteKey, turnstileSecretKey } : {}),
    loginSlug: normalizeOptional(values.loginSlug) || 'streammedia',
    locale: normalizeOptional(values.locale) || 'ru_RU',
    permalinkStructure: normalizeOptional(values.permalinkStructure) || '/%postname%/',
  };
  if (categories.length) payload.categories = categories;

  // Optional per-site design (palette/typography/texts/brandTokens). wpboot treats it as
  // wishes and falls back to a domain-seeded design when absent. Send only if non-empty.
  if (values.design && typeof values.design === 'object') {
    const design = values.design;
    if (design.palette || design.typography || design.texts || design.privacyPolicy || design.brandTokens) {
      payload.design = design;
    }
  }

  const formData = new FormData();
  formData.append('request', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  formData.append('favicon', values.favicon);
  formData.append('logo', values.logo);

  return formData;
}
